import { Button, Select, Tag, Tooltip } from 'antd';
import { Check, Pencil, X } from 'lucide-react';
import { useState } from 'react';
import type { RoleWithPermissions } from '../roles/roles.api';

interface UserRolesCellProps {
  roles: string[];
  roleOptions: RoleWithPermissions[];
  /** False on your own row — the server refuses a change to your own roles. */
  canEdit: boolean;
  onSave: (roles: string[]) => Promise<void>;
}

/** Read-only tags until "Edit" is clicked, then a multi-select with an explicit Save — never autosaves a role change. */
export function UserRolesCell({ roles, roleOptions, canEdit, onSave }: UserRolesCellProps) {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<string[]>(roles);
  const [saving, setSaving] = useState(false);

  function startEditing() {
    setSelected(roles);
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    try {
      await onSave(selected);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {roles.length === 0 && <span className="text-sm text-muted">No roles</span>}
        {roles.map((role) => (
          <Tag key={role}>{role}</Tag>
        ))}
        <Tooltip title={canEdit ? undefined : "You can't change your own roles"}>
          <Button type="text" size="small" icon={<Pencil className="size-3.5" />} disabled={!canEdit} onClick={startEditing} aria-label="Edit roles" />
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <Select
        mode="multiple"
        size="small"
        className="min-w-48"
        value={selected}
        onChange={setSelected}
        options={roleOptions.map((role) => ({ value: role.key, label: role.name }))}
        disabled={saving}
        autoFocus
      />
      <Button type="text" size="small" icon={<Check className="size-3.5" />} loading={saving} onClick={save} aria-label="Save roles" />
      <Button type="text" size="small" icon={<X className="size-3.5" />} disabled={saving} onClick={() => setEditing(false)} aria-label="Cancel" />
    </div>
  );
}
